import { ExternalLink, type LucideIcon, MoreHorizontal } from "lucide-react";
import * as React from "react";
import { strings } from "@/I18n";
import { Button } from "@/v2/ui/primitives/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/v2/ui/primitives/dropdown-menu";

/**
 * Menu item configuration for ActionMenu
 */
export interface ActionMenuItem<T = unknown> {
  /** Display label for the menu item */
  label: string;
  /** Optional icon to display before the label */
  icon?: LucideIcon;
  /** Click handler - receives the row data */
  onClick: (row: T) => void;
  /** Whether the item is disabled */
  disabled?: boolean | ((row: T) => boolean);
  /** Whether this is a destructive action (shows in red with separator) */
  destructive?: boolean;
  /** Conditional visibility - return false to hide this item */
  show?: (row: T) => boolean;
  /** Whether clicking this item opens an external link */
  external?: boolean;
}

/**
 * Configuration for ActionMenu in ResponsiveDataView
 */
export interface ActionMenuConfig<T> {
  /** Array of menu items */
  items: ActionMenuItem<T>[];
  /** Alignment of the dropdown menu */
  align?: "start" | "center" | "end";
}

export interface ActionMenuProps<T> {
  /** The row data this menu is for */
  row: T;
  /** Menu configuration */
  config: ActionMenuConfig<T>;
  /** Optional className for the trigger button */
  className?: string;
}

/**
 * Reusable action menu component for table rows.
 * Renders a dropdown menu with configurable items, handling:
 * - Conditional visibility via `show` function
 * - Disabled state (static or dynamic via function)
 * - Destructive items with separators and red styling
 * - Icons before labels
 */
export function ActionMenu<T>({ row, config, className }: ActionMenuProps<T>) {
  const { items, align = "end" } = config;

  // Filter visible items
  const visibleItems = items.filter((item) => {
    if (item.show) {
      return item.show(row);
    }
    return true;
  });

  // Don't render if no visible items
  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild={true}>
        <Button
          variant="outline"
          size="sm"
          className={className ?? "tw-h-8 tw-px-2"}
          aria-label={strings("common.rowActions")}
        >
          <MoreHorizontal className="tw-h-4 tw-w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align={align}>
        {visibleItems.map((item, index) => {
          const Icon = item.icon;
          const isDisabled =
            typeof item.disabled === "function"
              ? item.disabled(row)
              : item.disabled;
          const isDestructive = item.destructive;

          // Add separator before destructive items (unless it's the first item)
          const showSeparator = isDestructive && index > 0;

          return (
            <React.Fragment key={item.label}>
              {showSeparator && <DropdownMenuSeparator />}
              <DropdownMenuItem
                onClick={() => item.onClick(row)}
                disabled={isDisabled}
                className={
                  isDestructive
                    ? "tw-text-destructive focus:tw-text-destructive"
                    : undefined
                }
              >
                {Icon && <Icon className="tw-mr-2 tw-h-4 tw-w-4" />}
                {item.label}
                {item.external && (
                  <ExternalLink className="tw-ml-2 tw-h-3 tw-w-3 tw-text-muted-foreground" />
                )}
              </DropdownMenuItem>
            </React.Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

ActionMenu.displayName = "ActionMenu";
